import Scheduler from "../controllers/scheduler";
import StatefulSettings from "../controllers/StatefulSettings";
import Cookies from "../utilities/cookies";
import Timer from "../utilities/Timer";
import Database from "../utilities/database";
import {
  ISettingsItem,
  IPermissionItem,
  ITaskItem,
  EventItem,
  PermissionItem,
  ItemAdditionOptions,
  IPenaltyItem,
  IEventItem,
  ITypingStats,
  ITaskVoteConfig,
  IRouletteRequest,
  IRequestLockState,
  IShowNotificationState,
  AuditMessage,
} from "../ts/Definitions";
import { ItemTypes, AuditActionTypes, TraceLevel } from "../ts/enums";
import Emitter, { ShowHelp } from "../utilities/Emitter";

export class SchedulerManager {
  private scheduler?: Scheduler;
  private schedulerRunning = false;
  private schedulerInitializing = false;
  private housekeepingRan = false;
  private userSettings?: StatefulSettings;
  private cookiesService!: Cookies;
  private minuteTimer!: Timer;
  private database!: Database;
  public showMessage: (args: IShowNotificationState) => void = () => ({});
  private displayMode: string = "browser";

  private auditDailyRollInitSkipped(reason: string): void {
    if (!this.userSettings) return;
    this.userSettings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "Daily Roll Init Skipped",
        reason,
        TraceLevel.info
      )
    );
  }

  public initialize(
    cookiesService: Cookies,
    minuteTimer: Timer,
    database: Database,
    displayMode: string
  ): void {
    this.cookiesService = cookiesService;
    this.minuteTimer = minuteTimer;
    this.database = database;
    this.displayMode = displayMode;
  }

  public setUserSettings(userSettings?: StatefulSettings, isAdmin = false): void {
    this.userSettings = userSettings;
    if (userSettings && !this.housekeepingRan) {
      this.runStartupHousekeeping(isAdmin);
    }
  }

  private runStartupHousekeeping(isAdmin: boolean): void {
    if (!this.userSettings || !this.database || this.housekeepingRan) return;
    this.housekeepingRan = true;
    this.userSettings.deleteOldMessages(true);
    this.userSettings.deleteOldSentMessages(true);
    void this.database.clearOldProofRequests(this.userSettings.getControl().userid).catch((error) =>
      console.error("Failed to clear old proof requests:", error)
    );
    if (isAdmin) {
      void this.database.deleteOldVotes().catch((error) =>
        console.error("Failed to delete old votes:", error)
      );
      void this.database.deleteOldPillories().catch((error) =>
        console.error("Failed to delete old pillories:", error)
      );
      void this.database.deleteOldLocks().catch((error) =>
        console.error("Failed to delete old locks:", error)
      );
    }
  }

  public setShowMessage(
    showMessage: (args: IShowNotificationState) => void
  ): void {
    this.showMessage = showMessage;
  }

  /**
   * Creates and initializes a new scheduler instance
   */
  private createAndInitScheduler(): void {
    this.scheduler = new Scheduler(
      this.showMessage,
      this.userSettings!,
      this.cookiesService,
      this.minuteTimer
    );
    this.scheduler.init();
  }

  /**
   * Creates the scheduler for the current session
   */
  public async createScheduler(
    isAdmin: boolean,
    onStateAvailable: () => void,
    notifyUserSettingsCallbacks: () => void
  ): Promise<void> {
    if (!this.userSettings) return;
    if (!this.userSettings.isLoaded()) {
      this.auditDailyRollInitSkipped(
        "User settings not loaded yet; scheduler creation postponed"
      );
      return;
    }
    if (this.schedulerInitializing) {
      this.auditDailyRollInitSkipped(
        "Scheduler is already initializing"
      );
      return;
    }
    if (this.scheduler || this.schedulerRunning) {
      return;
    }

    this.schedulerInitializing = true;
    try {
      this.schedulerRunning = true;
      await this.userSettings.updateSessionLock();
      if (!isAdmin) {
        this.createAndInitScheduler();
      }
      if (this.userSettings.isControlLoaded()) this.handleHeartbeat();
      else
        this.userSettings.subscribeToControlRecord(() =>
          this.controlRecordCallback(onStateAvailable)
        );

      if (isAdmin)
        await this.database.rollAudit(this.userSettings.getControl().userid);
      notifyUserSettingsCallbacks();
    } catch (error) {
      this.schedulerRunning = false;
      this.scheduler = undefined;
      throw error;
    } finally {
      this.schedulerInitializing = false;
    }
  }

  private controlRecordCallback(onStateAvailable: () => void): void {
    this.userSettings?.unsubscribeToControlRecord(() =>
      this.controlRecordCallback(onStateAvailable)
    );
    this.handleHeartbeat();
  }

  private handleHeartbeat(): void {
    if (!this.userSettings) return;
    const { heartbeat } = this.userSettings.getControl();
    const currentVersion = import.meta.env.VITE_VERSION || "1.0.0";
    if (
      heartbeat &&
      heartbeat !== "newUser" &&
      currentVersion !== heartbeat
    ) {
      const version = currentVersion.split(".");
      const heartbeatVersion = this.userSettings
        ?.getControl()
        .heartbeat.split(".");
      this.userSettings.updateControlAttributes({
        displayMode: this.displayMode,
      });
      const major = parseInt(version[0]);
      const minor = parseInt(version[1]);
      const hMajor = parseInt(heartbeatVersion[0]);
      const hMinor = parseInt(heartbeatVersion[1]);
      if (hMajor !== major || hMinor !== minor)
        Emitter.emit(ShowHelp, "New Features");
    }
    this.userSettings?.heartbeat();
  }

  /**
   * Sets admin mode
   */
  public async setAdminMode(isAdmin: boolean): Promise<void> {
    if (!this.userSettings) return;
    this.userSettings.setAdminMode(isAdmin);
    if (this.scheduler) {
      this.scheduler.closeDown();
    }
    if (isAdmin) {
      this.scheduler = undefined;
      await this.userSettings.updateSessionLock();
    } else {
      this.createAndInitScheduler();
    }
  }

  /**
   * Cleanup scheduler
   */
  public cleanupScheduler(): void {
    if (this.scheduler) this.scheduler.closeDown();
    this.scheduler = undefined;
    this.schedulerRunning = false;
    this.housekeepingRan = false;
  }

  // Scheduler delegation methods
  public addPermissionToSchedule(
    checkoutPerm: IPermissionItem,
    scheduleReason: string
  ): void {
    if (!this.scheduler) return;
    void this.scheduler.addPermissionToSchedule(checkoutPerm, scheduleReason);
  }

  public handleItemFailed(item: ISettingsItem): void {
    if (!this.scheduler) return;
    this.scheduler.handleItemFailed(item);
  }

  public handlePlayItem(item: ISettingsItem): void {
    if (!this.scheduler) return;
    this.scheduler.onPlayItem(item);
  }

  public onAddToSchedule(item: ISettingsItem, scheduleReason: string): void {
    if (!this.scheduler) return;
    void this.scheduler.onAddToSchedule(item, scheduleReason);
  }

  public addTaskToSchedule(
    inputItem: ITaskItem,
    sourceItem: EventItem | PermissionItem | undefined,
    scheduleReason: string,
    inputOptions?: ItemAdditionOptions
  ): any {
    if (!this.scheduler) return;
    return this.scheduler
      .getSettings()
      .addTaskToSchedule(inputItem, scheduleReason, sourceItem, inputOptions);
  }

  public addPenaltyToSchedule(
    inputItem: IPenaltyItem,
    sourceItem: EventItem | PermissionItem | undefined,
    scheduleReason: string,
    inputOptions?: ItemAdditionOptions
  ): any {
    if (!this.scheduler) return;
    return this.scheduler
      .getSettings()
      .addPenaltyToSchedule(inputItem, scheduleReason, sourceItem, inputOptions);
  }

  public refreshStartOfDaySchedule(eventItem: IEventItem): void {
    if (!this.scheduler) return;
    void this.scheduler.refreshStartOfDaySchedule(eventItem);
  }

  public addSystemGeneratedPenaltyItem(item: IPenaltyItem, reason: string): void {
    if (!this.scheduler) return;
    void this.scheduler.addSystemGeneratedPenaltyItem(item, reason);
  }

  public setStateToCurrentDay(): any {
    if (!this.scheduler) return;
    return this.scheduler.setStateToCurrentDay();
  }

  public sendLines(
    lines: string,
    line: string,
    expectedCount: number,
    mistakes: number,
    itemKey: string,
    allowRedoLines: boolean,
    stats: ITypingStats
  ): any {
    if (!this.scheduler) return;
    return this.scheduler.sendLines(
      lines,
      line,
      expectedCount,
      mistakes,
      itemKey,
      allowRedoLines,
      stats
    );
  }

  /**
   * Opens the roulette dialog without requiring a running scheduler.
   * Used by Snakes & Ladders (and any other context without a live scheduler).
   */
  public handleRouletteTask(
    item: ITaskItem | IPenaltyItem,
    options?: string[]
  ): void {
    if (!this.userSettings) return;
    this.userSettings.updateRouletteDialog({
      options,
      open: true,
      rouletteTask: item,
      title: item.name,
      description: item.description,
      itemType: item.itemType,
    });
  }

  public setRouletteResult(
    item: string | ITaskItem | IPenaltyItem | ITaskVoteConfig,
    rouletteRequest: IRouletteRequest,
    options?: ItemAdditionOptions
  ): any {
    if (!this.scheduler) {
      if (!this.userSettings) return;
      this.userSettings.updateRouletteDialog(false);

      // Handle option-selection roulette without a live scheduler (e.g. Snakes & Ladders).
      // The caller must have added the basket item to taskSchedule beforehand via updateTaskState.
      if (rouletteRequest.options || rouletteRequest.rouletteTask?.options) {
        const key = rouletteRequest.rouletteTask?.key;
        if (!key) return;
        const schedule = this.userSettings.getTaskSchedule();
        const entry = schedule[key];
        if (entry) {
          (entry as unknown as { selectedCondition?: string }).selectedCondition =
            item?.toString();
          this.userSettings.updateTaskState(
            entry as unknown as Parameters<StatefulSettings["updateTaskState"]>[0]
          );
        }
        return;
      }

      if ((item as ITaskItem | IPenaltyItem).itemType === ItemTypes.task) {
        this.userSettings.addTaskToSchedule(
          item as ITaskItem,
          "This task was added to the session from a result of a roulette wheel spun by the member. The tasks are within the limits of the member and valid today based on the members diary",
          undefined,
          { ...(options ?? {}), isRoulette: true }
        );
        return;
      }

      if ((item as ITaskItem | IPenaltyItem).itemType === ItemTypes.penalty) {
        this.userSettings.addPenaltyToSchedule(
          item as IPenaltyItem,
          "This task was added to the session from a result of a roulette wheel spun by the member. The tasks are within the limits of the member and valid today based on the members diary",
          undefined,
          { ...(options ?? {}), isRoulette: true }
        );
        return;
      }

      if ((item as ITaskVoteConfig).taskKey) {
        this.userSettings.voteUpdate(item as ITaskVoteConfig);
      }
      return;
    }
    return this.scheduler.handleRouletteResult(item, rouletteRequest, options);
  }

  public saveMorningReport(reviewText: string): any {
    if (!this.scheduler) return;
    return this.scheduler.saveMorningReport(reviewText);
  }

  public saveDailyReport(reviewText: string): any {
    if (!this.scheduler) return;
    return this.scheduler.saveDailyReport(reviewText);
  }

  public setRequestLockComplete(requestLockState: IRequestLockState): any {
    if (!this.scheduler) return;
    return this.scheduler.saveLockResult(requestLockState);
  }
}
